// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Styles from './orderSummary.module.scss';
import { SetItemCostToTwoDecimalPlaces } from 'src/helpers/currencyManipulation';
import { IClaimOrder, ILineItem } from './../../businessLogic/sagas/serviceRequest';
import { getLineItemName } from './../../components/LineItem';

interface PropType {
    confirmation: IClaimOrder;
}

export default function OrderSummary(props: PropType) {
    return (
        <section className={Styles.item}>
            <OrderLineItemSummary claimOrder={props.confirmation} />
            <CostLineItemSummary claimOrder={props.confirmation} />
        </section>
    );
}

function OrderLineItems({ claimOrder }: { claimOrder: IClaimOrder }) {
    if (claimOrder.lineItems.length === 0) return null;

    const currency = claimOrder.payments.currency;
    
    return (
        <div className={Styles.lineItems}>
            {claimOrder.lineItems.map((lineItem: ILineItem, key: number) => {
                const cost = lineItem.repairOffer.listPrice || 0;
                const { notification = '', priority = '' } = lineItem.progress;
                const orderType = `${notification}${priority}`;
                if(claimOrder.serviceRequests.length > 1 && orderType === 'Z1H') return null;
                return (
                    <div className={Styles.group} key={key}>
                        <RenderRow
                            cost={cost}
                            currency={currency}
                            text={getLineItemName(lineItem)}
                        />
                        <RenderRow
                            cost={lineItem.shippingOffer.listPrice}
                            currency={currency}
                            text={lineItem.shippingOffer.name}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function CostLineItem({cost, currency, text}: {cost: number, currency: string, text: string}) {
    return (
        <div className={clsx(Styles.row, Styles.paddingTop5)}>
            <div className={clsx(Styles.colOffSet8, Styles.col2)}>{`${text}:`}</div>
            <div className={Styles.col2}>
                {`${currency} ${SetItemCostToTwoDecimalPlaces({cost})}`}
            </div>
        </div>
    );
}

function OrderLineItemSummary({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();
    return (
        <section className={Styles.marginTop15}>
            <div className={clsx(Styles.row, Styles.borderBottom)}>
                <h2 className={clsx(Styles.col, Styles.h2Heading)}>{t('order_summary')}</h2>
            </div>
            <OrderLineItems claimOrder={claimOrder} />
        </section>
    );
}

function CostLineItemSummary({ claimOrder }: { claimOrder: IClaimOrder }) {
    const { t } = useTranslation();
    const {
        currency,
        totalAmount,
        totalShippingAmount,
        totalTaxAmount,
    } = claimOrder.payments;
    return (
        <section className={clsx(Styles.borderBottom, Styles.borderTop, Styles.marginBottom30, Styles.paddingVertical7)}>
            <CostLineItem
                cost={totalTaxAmount}
                currency={currency}
                text={t('tax')}
            />
            <CostLineItem
                cost={totalShippingAmount}
                currency={currency}
                text={t('shipping')}
            />
            <CostLineItem
                cost={totalAmount}
                currency={currency}
                text={t('total')}
            />
        </section>
    );
}

function RenderRow(
    {cost, currency, text}:
    {cost: number, currency: string, text: string}
) {
    return (
        <div className={clsx(Styles.row)}>
            <div className={clsx(Styles.col8, Styles.paddingLeft10)}>{text}</div>
            <div className={clsx(Styles.colOffSet2, Styles.col2)}>{
                `${currency} ${SetItemCostToTwoDecimalPlaces({ cost })}`}
            </div>
        </div>
    );
}